import axiosInstance from '../../../utils/axiosConfig';
// API URL for backend requests
const API_URL = '/api/budget';

/**
 * Budget recurrences service functions
 */

/**
 * Fetch all budget templates
 * @returns {Promise} Promise with budget templates data
 */
export const getBudgetTemplates = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/templates`);
    return response;
  } catch (error) {
    console.error('Error fetching budget templates:', error);
    throw error;
  }
};

/**
 * Create a new budget template
 * @param {Object} templateData - Budget template data
 * @returns {Promise} Promise with created template data
 */
export const createBudgetTemplate = async (templateData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/templates`, templateData);
    return response;
  } catch (error) {
    console.error('Error creating budget template:', error);
    throw error;
  }
};

/**
 * Update a budget template
 * @param {string} templateId - Template ID
 * @param {Object} templateData - Updated template data
 * @returns {Promise} Promise with updated template data
 */
export const updateBudgetTemplate = async (templateId, templateData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/templates/${templateId}`, templateData);
    return response;
  } catch (error) {
    console.error('Error updating budget template:', error);
    throw error;
  }
};

/**
 * Delete a budget template
 * @param {string} templateId - Template ID to delete
 * @returns {Promise} Promise with delete result
 */
export const deleteBudgetTemplate = async (templateId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/templates/${templateId}`);
    return response;
  } catch (error) {
    console.error('Error deleting budget template:', error);
    throw error;
  }
};

/**
 * Fetch all budget recurrences
 * @returns {Promise} Promise with budget recurrences data
 */
export const getBudgetRecurrences = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/recurrences`);
    return response;
  } catch (error) {
    console.error('Error fetching budget recurrences:', error);
    throw error;
  }
};

/**
 * Get a single budget recurrence by ID
 * @param {string} recurrenceId - Recurrence ID
 * @returns {Promise} Promise with recurrence data
 */
export const getBudgetRecurrence = async (recurrenceId) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/recurrences/${recurrenceId}`);
    return response;
  } catch (error) {
    console.error('Error fetching budget recurrence:', error);
    throw error;
  }
};

/**
 * Create a new budget recurrence
 * @param {Object} recurrenceData - Budget recurrence data
 * @returns {Promise} Promise with created recurrence data
 */
export const createBudgetRecurrence = async (recurrenceData) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/recurrences`, recurrenceData);
    return response;
  } catch (error) {
    console.error('Error creating budget recurrence:', error);
    throw error;
  }
};

/**
 * Update a budget recurrence
 * @param {string} recurrenceId - Recurrence ID
 * @param {Object} recurrenceData - Updated recurrence data
 * @returns {Promise} Promise with updated recurrence data
 */
export const updateBudgetRecurrence = async (recurrenceId, recurrenceData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/recurrences/${recurrenceId}`, recurrenceData);
    return response;
  } catch (error) {
    console.error('Error updating budget recurrence:', error);
    throw error;
  }
};

/**
 * Delete a budget recurrence
 * @param {string} recurrenceId - Recurrence ID to delete
 * @returns {Promise} Promise with delete result
 */
export const deleteBudgetRecurrence = async (recurrenceId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/recurrences/${recurrenceId}`);
    return response;
  } catch (error) {
    console.error('Error deleting budget recurrence:', error);
    throw error;
  }
};

/**
 * Execute a budget recurrence manually (run now)
 * @param {string} recurrenceId - Recurrence ID to execute
 * @returns {Promise} Promise with execution result
 */
export const executeBudgetRecurrence = async (recurrenceId) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/recurrences/${recurrenceId}/execute`);
    return response;
  } catch (error) {
    console.error('Error executing budget recurrence:', error);
    throw error;
  }
};

/**
 * Pause/unpause a budget recurrence
 * @param {string} recurrenceId - Recurrence ID
 * @param {boolean} active - Whether to activate or pause
 * @returns {Promise} Promise with updated status
 */
export const toggleBudgetRecurrenceStatus = async (recurrenceId, active) => {
  try {
    const response = await axiosInstance.patch(`${API_URL}/recurrences/${recurrenceId}/status`, { active });
    return response;
  } catch (error) {
    console.error('Error toggling budget recurrence status:', error);
    throw error;
  }
};

export const getAllBudgets = async () => {
  try {
    console.log('🔄 Budget Service: Fetching all budgets...');
    const response = await axiosInstance.get(`${API_URL}`);
    console.log('✅ Budget Service: Budgets retrieved:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Budget Service: Error fetching all budgets:', error);
    // Return empty array instead of throwing to help fallback logic
    return [];
  }
};

export const getBudgetById = async (id) => {
  const response = await axiosInstance.get(`${API_URL}/${id}`);
  return response.data;
};

export const createBudget = async (budgetData) => {
  const response = await axiosInstance.post(`${API_URL}`, budgetData);
  return response.data;
};

export const updateBudget = async (id, budgetData) => {
  const response = await axiosInstance.put(`${API_URL}/${id}`, budgetData);
  return response.data;
};

export const deleteBudget = async (id) => {
  const response = await axiosInstance.delete(`${API_URL}/${id}`);
  return response.data;
};

export const cloneBudget = async (id) => {
  const response = await axiosInstance.post(`${API_URL}/${id}/clone`);
  return response.data;
};

export const compareBudgets = async (id, compareId) => {
  const response = await axiosInstance.get(`${API_URL}/${id}/compare?with=${compareId}`);
  return response.data;
};

export const getActualSpending = async (id) => {
  const response = await axiosInstance.get(`${API_URL}/${id}/actual`);
  return response.data;
};

export const getBudgetStatistics = async () => {
  try {
    console.log('🔄 Budget Service: Calculating statistics from budgets...');
    // Don't use the /stats endpoint which is broken
    // Fetch budgets and calculate stats directly
    const budgets = await getAllBudgets();
    
    if (!Array.isArray(budgets) || budgets.length === 0) {
      console.log('ℹ️ Budget Service: No budgets found, returning zeros');
      return {
        totalBudgeted: 0,
        spent: 0,
        remaining: 0,
        savingsRate: 0,
        budgetCount: 0,
        categoryCoverage: 0,
        utilization: 0
      };
    }
    
    // Find active budget
    const activeBudget = budgets.find(b => b.isPrimary || b.isActive) || budgets[0];
    console.log('📊 Budget Service: Using budget for calculation:', activeBudget);
    
    // Get planned values directly from the budget object
    const totalPlannedIncome = parseFloat(activeBudget.totalPlannedIncome || 0);
    const totalPlannedExpenses = parseFloat(activeBudget.totalPlannedExpenses || 0);
    
    console.log('📊 Budget direct fields - Income:', totalPlannedIncome, 'Expenses:', totalPlannedExpenses);
    
    // Check if we have top-level fields with actual totals
    let totalBudgeted = totalPlannedExpenses; // Use the planned expenses as the total budgeted amount
    let spent = 0; // We don't have actual spent data at this level
    
    // As a fallback, try to calculate from categories if available
    if ((!totalBudgeted || totalBudgeted === 0) && activeBudget.categories) {
      console.log('📊 Falling back to category calculations...');
      const categories = Array.isArray(activeBudget.categories) ? activeBudget.categories : [];
      
      totalBudgeted = categories.reduce((sum, cat) => {
        const budgeted = cat && cat.budgeted ? parseFloat(cat.budgeted) : 0;
        return sum + (isNaN(budgeted) ? 0 : budgeted);
      }, 0);
      
      spent = categories.reduce((sum, cat) => {
        const actual = cat && cat.actual ? parseFloat(cat.actual) : 0;
        return sum + (isNaN(actual) ? 0 : actual);
      }, 0);
    }
    
    // If we still don't have spent, use 0 for now
    if (spent === 0) {
      console.log('📊 No spent data found, using 0');
    }
    
    const remaining = totalBudgeted - spent;
    
    // Calculate savings rate (savings / income)
    // Assume 15% of remaining is savings if we can't find explicit savings categories
    const savingsAmount = remaining * 0.15;
    const savingsRate = totalPlannedIncome > 0 ? (savingsAmount / totalPlannedIncome) * 100 : 0;
    
    const stats = {
      totalBudgeted,
      spent,
      remaining,
      savingsRate,
      budgetCount: budgets.length,
      // Use 100% for category coverage since we're now using top-level budget numbers
      categoryCoverage: 100,
      utilization: totalBudgeted > 0 ? (spent / totalBudgeted) * 100 : 0
    };
    
    console.log('✅ Budget Service: Calculated statistics:', stats);
    return stats;
  } catch (error) {
    console.error('❌ Budget Service: Error calculating statistics:', error);
    // Return default values instead of throwing
    return {
      totalBudgeted: 0,
      spent: 0,
      remaining: 0,
      savingsRate: 0,
      budgetCount: 0,
      categoryCoverage: 0,
      utilization: 0
    };
  }
};

// Create a default export object that includes all the functions
const budgetService = {
  getBudgetTemplates,
  createBudgetTemplate,
  updateBudgetTemplate,
  deleteBudgetTemplate,
  getBudgetRecurrences,
  getBudgetRecurrence,
  createBudgetRecurrence,
  updateBudgetRecurrence,
  deleteBudgetRecurrence,
  executeBudgetRecurrence,
  toggleBudgetRecurrenceStatus,
  getAllBudgets,
  getBudgetById,
  createBudget,
  updateBudget,
  deleteBudget,
  cloneBudget,
  compareBudgets,
  getActualSpending,
  getBudgetStatistics
};

export default budgetService;

// Keep the named exports as well
// export {
//   getBudgetTemplates,
//   createBudgetTemplate,
//   updateBudgetTemplate,
//   deleteBudgetTemplate,
//   getBudgetRecurrences,
//   getBudgetRecurrence,
//   createBudgetRecurrence,
//   updateBudgetRecurrence,
//   deleteBudgetRecurrence,
//   executeBudgetRecurrence,
//   toggleBudgetRecurrenceStatus,
//   getAllBudgets,
//   getBudgetById,
//   createBudget,
//   updateBudget,
//   deleteBudget,
//   cloneBudget,
//   compareBudgets,
//   getActualSpending,
//   getBudgetStatistics
// }; 